<script setup lang="ts">
import type { KjdsDetailPageProps } from '@/types/appTypes'

const props = withDefaults(defineProps<KjdsDetailPageProps>(), {})

const app = useNuxtApp()
const storeIndex = useStoreIndex()
const storeArticleDetail = useStoreArticleDetail()

const baseTitle = '巡回展'
let headTitle = ''
let headDescription = ''

const pageData = reactive({
  // logoSocialLine: await import('@/assets/images/logo_social_line.png'),
  tEyeCatch: null, // アイキャッチ情報
  aSlide: null, // スライド情報
  sSlideCaption: null, // スライドキャプション
  aEventInfo: null, // イベント情報
  aEventItems: null,
  presentItems: null,
  sOfficialLinkUrl: null, // 公式サイトURL
  sBody: null, // ボディ
})

const setHead = () => {
  const articleTitle = props.kjdsDetailData.article.title
  headTitle = `${articleTitle} | ${baseTitle}`
  headDescription = 'アイエム［インターネットミュージアム］による'
    + articleTitle
    + 'の巡回展情報ページ。'
    + articleTitle
    + 'の見どころ、会場、会期、料金、休館日などを紹介しています。'
    + articleTitle
    + 'の巡回展情報はアイエムで！'
  if (props.kjdsDetailData.article.description?.length) {
    headDescription = props.kjdsDetailData.article.description
  }
  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: baseTitle,
      disabled: false,
      to: { path: '/jyunkai' },
    },
    {
      text: articleTitle,
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
    ogImage: props.kjdsDetailData.eyeCatch?.url ? props.kjdsDetailData.eyeCatch.url : undefined,
  })
  useHead(headInput)
}

const onChangeSlide = (slideNO) => {
  pageData.sSlideCaption = pageData.aSlide[slideNO].sCaption
}

const initPage = async () => {
  // 巡回展から表示カラム作成
  if (!pageData.tEyeCatch) {
    pageData.tEyeCatch = app.$getEyeCatch(props.kjdsDetailData.article)
  } // アイキャッチ情報作成
  if (!pageData.sBody) {
    pageData.sBody = app.$getArticleBody(props.kjdsDetailData.article)
    if (app.$isArticleOld(props.kjdsDetailData.article)) {
      pageData.sBody = pageData.sBody.replace(/.*(<table.*title="公式サイト".*<\/table>)/s, '')
    }
  }
  if (!pageData.sOfficialLinkUrl && props.kjdsDetailData.article.custom && props.kjdsDetailData.article.custom.official_site) {
    // 公式サイトURL
    pageData.sOfficialLinkUrl = props.kjdsDetailData.article.custom.official_site
  }

  // 巡回展からスライド作成
  if (import.meta.client === true) {
    if (!pageData.aSlide && props.kjdsDetailData.article) {
      if (props.kjdsDetailData.article.custom && props.kjdsDetailData.objects) { // 追加
        pageData.aSlide = []
        app.$addSlides(
          props.kjdsDetailData.article,
          'jyunkai_slide',
          10,
          props.kjdsDetailData.objects,
          pageData.aSlide,
          (props.kjdsDetailData.article.is_old === 1) ? null : 'l',
        )
      }
      if (props.kjdsDetailData.article.eye_catch_image_object) {
        app.$initSlide(props.kjdsDetailData.article.eye_catch_image_object, pageData.aSlide)
      }
    }
    if (pageData.aSlide && pageData.aSlide.length === 1) {
      onChangeSlide(0)
    }
  }

  // イベント情報作成
  if (!pageData.aEventInfo) {
    pageData.aEventInfo = []

    // 関連記事からイベント取得
    const aEventItem = app.$getArticleRelationsMulti(props.kjdsDetailData.article, 'event', null)
    if (aEventItem) {
      for (let i = 0; i < aEventItem.length; i++) {
        // イベント情報追加
        pageData.aEventInfo.push({
          sEventArticleSlug: aEventItem[i].article_slug, // イベントスラッグ
          sPrefecture: storeIndex.getPrefName(aEventItem[i].custom.custom_museum_prefecture), // 都道府県
          sVenue: app.$getEventVenue(aEventItem[i], true), // 会場
          sMuseumArticleSlug: app.$getEventMuseumArticleSlug(aEventItem[i]), // ミュージアムスラッグ
          sPeriodLabel: app.$getEventPeriodLabel(aEventItem[i]), // 会期
          sPeriodRemain: aEventItem[i].day_count_display !== 1 ? null : app.$getEventPeriodRemain(aEventItem[i]), // 開催中情報
          term_from: aEventItem[i].term_from, // 会期開始日付
        })

        // 公式サイトURL
        // if (!this.sOfficialLinkUrl && aEventItem[i].custom) {
        //   this.sOfficialLinkUrl = aEventItem[i].custom.event_url
        // }
      }

      // 開始日付でソート
      pageData.aEventInfo = app.$sortArticleTermFrom(pageData.aEventInfo, false)
    }
  }

  // 巡回展に紐づく展覧会を取得
  if (!pageData.aEventItems) {
    pageData.aEventItems = app.$getArticleRelationsMulti(
      props.kjdsDetailData.article,
      'event',
      null,
    )
  }

  const map = new Map<string, Article>()

  const results = await Promise.all(
    (pageData.aEventItems ?? []).map(eventItem =>
      storeArticleDetail.fetchDetail({
        fetchParams: {
          articleTypeSlug: 'event',
          articleSlugOrId: eventItem.article_slug,
        },
        isNoThrowError: true,
      }),
    ),
  )

  // 取得した展覧会詳細に紐づくプレゼントを抽出
  for (const res of results) {
    const eventDetail = res?.data?.article

    const presents = app.$getArticleRelationsMulti(eventDetail, 'present', null)
    presents.forEach(p => map.set(p.article_slug, p))
  }

  pageData.presentItems = app.$sortArticleOpenDate([...map.values()], true)
}
await initPage()
setHead()
</script>

<template>
  <div class="p-articleDetailPage">
    <div
      class="l-mainOverview"
    >
      <div class="c-header">
        <div>
          <p class="c-header_title">
            巡回展
          </p>
        </div>
      </div>

      <div class="c-infoDetail">
        <div class="c-infoDetail_component">
          <div
            v-if="pageData.tEyeCatch"
            class="c-infoDetail_component_left"
          >
            <div v-if="pageData.tEyeCatch.oImg">
              <CommonImage
                class="c-infoDetail_component_left_image"
                v-bind:src="pageData.tEyeCatch.oImg"
                error-src="/images/noimage.svg"
                v-bind:position="app.$device.isMobile ? 'center' : 'top center'"
                v-bind:height="app.$device.isMobile ? '280' : ''"
                object-fit="contain"
              />
            </div>
            <div
              v-if="pageData.tEyeCatch.sCaption"
              class="c-infoDetail_component_left_copy"
            >
              {{ pageData.tEyeCatch.sCaption }}
            </div>
          </div>
          <div class="c-infoDetail_component_right">
            <div
              v-if="props.kjdsDetailData.article.title"
              class="c-infoDetail_component_right_title"
            >
              {{ props.kjdsDetailData.article.title }}
            </div>
            <div
              v-for="(record, index) in pageData.aEventInfo"
              v-bind:key="index"
              class="c-infoDetail_component_right_detail"
            >
              <div class="c-infoDetail_component_right_detail_city">
                <div class="c-infoDetail_component_right_detail_city_box">
                  <span
                    v-if="record.sPrefecture"
                    class="c-infoDetail_component_right_detail_city_box_label"
                  >
                    {{ record.sPrefecture }}
                  </span>
                </div>
              </div>
              <div class="c-infoDetail_component_right_detail_info">
                <div class="c-infoDetail_component_right_detail_info_first">
                  <span
                    v-if="record.sPeriodLabel"
                    class="c-infoDetail_component_right_detail_info_first_status"
                  >
                    {{ record.sPeriodLabel }}
                  </span>
                  <span
                    v-if="record.sMuseumArticleSlug && record.sVenue"
                    class="c-infoDetail_component_right_detail_info_first_museum"
                  >
                    <nuxt-link
                      v-bind:to="`/museum/${record.sMuseumArticleSlug}`"
                    >
                      {{ record.sVenue }}
                    </nuxt-link>
                  </span>
                </div>
                <div class="c-infoDetail_component_right_detail_info_second">
                  <span
                    v-if="record.sPeriodRemain"
                    class="c-infoDetail_component_right_detail_info_second_duration"
                    v-bind:style="{ color: record.sPeriodRemain.color }"
                  >
                    {{ record.sPeriodRemain.label }}
                  </span>
                  <span class="c-infoDetail_component_right_detail_info_second_label">
                    <nuxt-link
                      v-bind:to="`/event/${record.sEventArticleSlug}`"
                    >
                      展覧会の詳細はこちら
                    </nuxt-link>
                  </span>
                </div>
              </div>
            </div>

            <div
              v-if="pageData.sOfficialLinkUrl"
              class="c-infoDetail_component_right_website"
            >
              <span class="c-infoDetail_component_right_website_label">
                公式サイト
              </span>
              <span class="c-infoDetail_component_right_website_url">
                <a
                  v-bind:href="pageData.sOfficialLinkUrl"
                  target="_blank"
                >
                  {{ pageData.sOfficialLinkUrl }}
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!--   carousel  -->
      <client-only>
        <div
          v-if="pageData.aSlide && pageData.aSlide.length > 0"
          class="c-carousel"
        >
          <v-carousel
            v-if="pageData.aSlide.length > 1"
            class="IMCustomCarousel"
            hide-delimiter-background
            cycle
            height="auto"
            v-on:update:model-value="onChangeSlide"
          >
            <v-carousel-item
              v-for="(record, index) in pageData.aSlide"
              v-bind:key="index"
            >
              <CommonImage
                object-fit="contain"
                error-src="/images/noimage.svg"
                v-bind:src="record.oImg"
                class="c-carousel-item-img"
              />
            </v-carousel-item>
          </v-carousel>
          <CommonImage
            v-else
            contain
            v-bind:src="pageData.aSlide[0].oImg"
            error-src="/images/noimage.svg"
            class="c-carousel-item-img"
            object-fit="contain"
          />
        </div>

        <div
          v-if="pageData.sSlideCaption"
          class="c-carousel_imgLocation"
        >
          {{ pageData.sSlideCaption }}
        </div>
      </client-only>

      <div class="c-body">
        <div
          v-if="pageData.sBody"
          class="c-body_text l-blank-icon-none"
          v-html="pageData.sBody"
        />
      </div>

      <div class="c-link">
        <ShareButtons
          v-bind:title="props.kjdsDetailData.article.title"
        />
      </div>
    </div>

    <RelationBox
      article="present"
      v-bind:header="'関連するプレゼント'"
      v-bind:articles="pageData.presentItems"
    />
    <RelationBox
      article="report"
      v-bind:header="'関連するレポート'"
      v-bind:relations="props.kjdsDetailData.article.relations"
      v-bind:objects="props.kjdsDetailData.objects"
    />

    <div class="c-pass">
      <div>
        <nuxt-link
          to="/jyunkai"
        >
          <v-btn
            width="400"
            variant="outlined"
            size="large"
            class="c-pass_button"
          >
            <v-icon start>
              mdi-chevron-right
            </v-icon>
            <div class="c-pass_button_text">
              巡回展一覧へ
            </div>
          </v-btn>
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as *;

.l-population {
  margin-top: 24px;
}

.c-listTag {
  margin: 46px 0 60px;
}

.c-pass {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px;
  margin-bottom: 80px;
  font-family: var(--g_font_family_SawarabiGothic);
  white-space: nowrap;

  a {
    text-decoration: none;
  }

  &_button {
    border: 2px solid var(--color_border_control);
    border-radius: 8px;
    font-size: 16px;
    font-weight: bold;
    color: var(--g_color_text_black);

    @include mq_down(sp) {
      width: auto !important;
    }
  }

  &_description {
    margin-top: 24px;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.571;
    text-align: center;
  }

  a:hover & {
    &_button {
      &_text {
        // text-decoration: underline;
        text-decoration: none;
      }
    }
  }
}

.l-partent {
  &_header {
    margin-bottom: 40px;
    padding-bottom: 19px;
    border-bottom: 1px solid var(--g_color_border_gray);

    @include mq_down(sp) {
      margin-bottom: 30px;
      padding-bottom: 9px;
    }

    &_text {
      margin-top: 15px;
      color: var(--color_text_base);
      font-family: var(--g_font_family_KozukaGothicPr6N);
      font-size: 20px;
      font-weight: 500;
      line-height: 0.923;

      @include mq_down(sp) {
        font-size: 18px;
        line-height: inherit;
      }

      &::before {
        content: "";
        display: inline-block;
        width: 26px;
        height: 26px;
        margin-top: -3px;
        margin-right: 5px;
        background-image: url("@/assets/images/icon-title.png");
        vertical-align: middle;

        @include mq_down(sp) {
          width: 20px;
          height: 20px;
          background-size: cover;
        }
      }
    }
  }
}

.c-header {
  display: flex;
  font-family: var(--g_font_family_NotoSansCJKjp);

  @include mq_down(sp) {
    margin: 0 0 16px;
  }

  &_avatar {
    width: 100px;
    height: 100px;
    margin-right: 20px;
    border: 1px dotted #ebebeb;
    object-fit: contain;
  }

  &_title {
    margin-top: 2px;
    color: var(--color_text_base);
    font-size: 25px;
    font-weight: 400;

    @include mq_down(sp) {
      margin: 0;
      font-size: 20px;
    }
  }
}

.c-infoDetail {
  // margin-top: 19px;
  // padding: 0 19px 0 19px;
  color: var(--color_text_base);
  font-family: var(--g_font_family_NotoSansCJKjp);
  font-size: 14px;
  line-height: 1.75;

  @include mq_down(sp) {
    // margin-top: 30px;
  }

  &_component {
    display: flex;
    justify-content: space-between;

    // margin-top: 30px;
    margin-top: 20px;
    margin-bottom: 30px;
    font-family: var(--g_font_family_NotoSansCJKjp);

    @include mq_down(sp) {
      display: block;

      // max-width: 400px;
      margin: 0 auto;
    }

    /*
    &_left {
      &_image {
        width: 162px;
        height: 220px;
        padding-right: 0;
        padding-left: 0;
        object-fit: contain;
        background-color: #ebebeb;

        @include mq_down(sp) {
          width: 100%;
          max-height: none;
        }
      }

      &_copy {
        color: var(--color_text_base);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 14px;
        line-height: 2;
      }
    }
    */
    &_left {
      width: 162px;

      @include mq_down(sp) {
        width: 100%;
      }

      &_image {
        flex-shrink: 0;
        width: 162px;
        max-height: 220px;
        padding-right: 0;
        padding-left: 0;

        @include mq_down(sp) {
          width: 100%;
          max-height: none;
        }
      }

      &_copy {
        margin-top: 5px;
        color: var(--color_text_base);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 12px;
        line-height: 2;
      }
    }

    &_right {
      width: 530px;
      margin-top: -8px;

      @include mq_down(sp) {
        width: 100%;
        margin: 20px 0 0;
      }

      &_title {
        font-size: 18px;
        font-weight: bold;
        word-spacing: 0;
      }

      &_detail {
        display: flex;
        align-items: flex-start;
        margin-top: 10px;

        &_city {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #747474;

          &_box {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 60px;
            height: 60px;
            background-color: var(--color_background_whiteblue);

            &_label {
              margin-top: 3px;
              color: #7d7d7d;
              font-size: 13px;
              font-weight: 400;
            }
          }
        }

        /*
        &_info {
          flex: 1;
          margin-top: 3px;
          margin-left: 10px;

          @include mq_down(sp) {
            margin-top: 0;
          }

          &_first {
            margin-bottom: 2px;

            &_status {
              font-size: 12px;

              @include mq_down(sp) {
                display: block;
              }
            }

            &_museum {
              color: var(--color_primary_link);
              font-size: 14px;

              @include mq_down(sp) {
                display: block;
                margin: 5px 0;
              }
            }
          }

          &_second {
            display: flex;
            margin-bottom: 2px;

            @include mq_down(sp) {
              flex-wrap: wrap;
            }

            &_border {
              position: relative;
              flex: 0 0 14px;
              width: 14px !important;
              height: 14px !important;
              margin-top: 5px;
              border: 1px solid var(--g_color_border_gray);
              border-radius: 50%;

              &_circle {
                position: absolute;
                top: 50%;
                left: 50%;
                width: 8px;
                height: 8px;
                transform: translate(-50%, -50%);
                border-radius: 50%;
              }
            }

            &_duration {
              font-size: 12px;
            }

            &_label {
              margin-left: 8px;
              color: var(--color_primary_link);
              font-size: 12px;

              @include mq_down(sp) {
                flex-basis: 100%;
                max-width: 100%;
                margin: 5px 0;
              }
            }
          }
        }
        */
        &_info {
          flex: 1;
          margin-top: 0;
          margin-left: 10px;

          &_first {
            margin-bottom: 2px;

            &_status {
              display: block;
              font-size: 12px;
            }

            &_museum {
              display: block;
              margin: 5px 0;
              color: var(--color_primary_link);
              font-size: 14px;
            }
          }

          &_second {
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 2px;

            &_duration {
              font-size: 12px;
            }

            &_label {
              flex-basis: 100%;
              max-width: 100%;
              margin: 5px 0;
              color: var(--color_primary_link);
              font-size: 12px;
            }
          }
        }
      }

      &_website {
        margin-top: 12px;

        @include mq_down(sp) {
          margin-top: 22px;
        }

        &_label {
          font-size: 14px;
          font-weight: bold;
        }

        &_url {
          margin-left: 10px;
          font-size: 14px;
        }
      }
    }
  }
}

.c-carousel {
  margin-top: 50px;

  &-item {
    &-img {
      position: relative;
      width: 100%;
      height: 347px;
      background-color: #ebebeb;

      @include mq_down(sp) {
        height: 48vw;
      }
    }
  }

  &_imgLocation {
    /*
    display: flex;
    margin-top: 5px;
    color: var(--color_text_base);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    line-height: 2;
    */
    display: flex;
    justify-content: flex-end;
    margin-top: 5px;
    color: var(--color_text_base);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    line-height: 2;
  }
}

.c-body {
  margin-top: 19px;
  color: var(--color_text_base);
  font-family: var(--g_font_family_NotoSansCJKjp);
  font-size: 16px;
  line-height: 1.75;

  &_text {
    // transform: rotate(0.03deg);
    // font-family: var(--g_font_family_MPuls);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 16px;
    line-height: 1.8;
  }
}

.c-link {
  display: flex;
  justify-content: center;

  // padding: 45px 20px 65px 25px;
  margin-top: 35px;

  &_rotate45deg {
    margin-left: 5px;
    transform: rotateZ(45deg);
    color: var(--g_color_background_black);
    font-size: 30px;
  }

  &_button {
    display: flex;

    // margin-top: -6px;
    // margin-left: -54px;

    @include mq_down(sp) {
      margin: 0;
    }

    &_border {
      min-width: 60px !important;
      min-height: 40px;

      &:hover {
        opacity: 0.7;
      }
    }
  }
}

.c-socialIcon {
  width: 18px;
  height: 18px;
}
</style>
